import { useEffect } from 'react';
import { ExternalLink, X } from 'lucide-react';
import type { DisclosureDetail } from './disclosures.types';
import { ReviewStatusBadge } from './DisclosuresTable';
import { DisclosureBodyView } from '../../components/DisclosureBodyView';
import '../../components/disclosureBodyView.css';

// 권위: ARCH_DASHBOARD §4.6 탭 6-A 상세 모달.
// 섹션: (1) 헤더 — 종목/제목/DART 링크 · (2) 본문 disclosure_body · (3) LLM 추출 결과 · (4) 메타
//
// 본문 최대 200,000자 대응: <pre> 에 max-height + overflow:auto. 가상화는 불필요 (브라우저 네이티브 스크롤로 충분).

interface Props {
  detail: DisclosureDetail | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onClose: () => void;
}

export function DisclosureDetailModal({
  detail,
  isLoading,
  isError,
  error,
  onClose,
}: Props): JSX.Element {
  // ESC 키로 닫기.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const body = detail?.disclosureBody ?? '';
  const bodyLen = body.length;

  return (
    <div
      className="syslog-modal__backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="syslog-modal disclosure-modal" role="dialog" aria-label="공시 상세">
        <header className="syslog-modal__header">
          {detail ? (
            <div className="syslog-modal__title-group">
              <span className="disclosure-symbol__code">{detail.symbol ?? '-'}</span>
              <span className="disclosure-modal__corp-name">
                {detail.corpName ?? <span className="syslog-muted">(법인명 없음)</span>}
              </span>
              <code className="syslog-modal__code">
                {detail.disclosureTitle ?? '(제목 없음)'}
              </code>
              <span className="syslog-modal__meta">
                {formatTs(detail.createdAt)}
              </span>
              {detail.disclosureUrl && (
                <a
                  href={detail.disclosureUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="disclosure-link disclosure-link--inline"
                  aria-label="DART 원문 열기"
                >
                  <ExternalLink size={14} aria-hidden="true" />
                  <span>DART 원문</span>
                </a>
              )}
            </div>
          ) : (
            <div className="syslog-modal__title-group">
              <span>공시 상세</span>
            </div>
          )}
          <button
            type="button"
            className="syslog-modal__close"
            onClick={onClose}
            aria-label="닫기"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <div className="syslog-modal__body">
          {isLoading && <div className="syslog-empty">불러오는 중…</div>}
          {isError && (
            <div className="syslog-warning-banner">
              상세 조회 실패: {error?.message ?? 'unknown'}
            </div>
          )}

          {detail && (
            <>
              <section className="syslog-section">
                <h4 className="syslog-section__title">
                  공시 본문{' '}
                  {bodyLen > 0 && (
                    <span className="syslog-muted">
                      · {bodyLen.toLocaleString('ko-KR')} 자
                    </span>
                  )}
                </h4>
                {bodyLen > 0 ? (
                  <DisclosureBodyView body={body} />
                ) : (
                  <div className="syslog-muted">
                    (본문 없음 — 과거 수집 실패 건이거나 공시 원문이 비어있음)
                  </div>
                )}
              </section>

              <section className="syslog-section">
                <h4 className="syslog-section__title">LLM 추출 결과</h4>
                {detail.extractedEvent ? (
                  <JsonBlock value={detail.extractedEvent} />
                ) : (
                  <div className="syslog-muted">(추출 결과 없음)</div>
                )}
                <dl className="syslog-meta">
                  <dt>llm_model</dt>
                  <dd>{detail.llmModel ?? <span className="syslog-muted">-</span>}</dd>
                  <dt>llm_confidence</dt>
                  <dd>
                    {detail.llmConfidence != null ? (
                      detail.llmConfidence.toFixed(2)
                    ) : (
                      <span className="syslog-muted">-</span>
                    )}
                  </dd>
                </dl>
              </section>

              <section className="syslog-section">
                <h4 className="syslog-section__title">검수 이력</h4>
                <dl className="syslog-meta">
                  <dt>review_status</dt>
                  <dd>
                    <ReviewStatusBadge status={detail.reviewStatus} />
                  </dd>
                  <dt>reviewer</dt>
                  <dd>{detail.reviewer ?? <span className="syslog-muted">-</span>}</dd>
                  <dt>reviewed_at</dt>
                  <dd>
                    {detail.reviewedAt ? (
                      formatTs(detail.reviewedAt)
                    ) : (
                      <span className="syslog-muted">-</span>
                    )}
                  </dd>
                  <dt>review_note</dt>
                  <dd>{detail.reviewNote ?? <span className="syslog-muted">-</span>}</dd>
                  <dt>rcpt_no</dt>
                  <dd>
                    <code className="disclosure-meta__mono">
                      {detail.rcptNo ?? <span className="syslog-muted">-</span>}
                    </code>
                  </dd>
                  <dt>corp_code</dt>
                  <dd>
                    <code className="disclosure-meta__mono">
                      {detail.corpCode ?? <span className="syslog-muted">-</span>}
                    </code>
                  </dd>
                </dl>
              </section>
            </>
          )}
        </div>

        <footer className="syslog-modal__actions">
          <button type="button" className="syslog-btn syslog-btn--ghost" onClick={onClose}>
            닫기
          </button>
        </footer>
      </div>
    </div>
  );
}

// SystemLogDetail 의 JsonBlock 과 동일 규약 (토큰·클래스 공유).
function JsonBlock({ value }: { value: unknown }): JSX.Element {
  let json: string;
  try {
    json = JSON.stringify(value, null, 2);
  } catch {
    json = String(value);
  }
  return <pre className="syslog-json">{highlightJson(json)}</pre>;
}

function highlightJson(json: string): JSX.Element[] {
  const tokenRe =
    /("(?:\\.|[^"\\])*")\s*:|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
  const out: JSX.Element[] = [];
  let lastIndex = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(json)) !== null) {
    if (m.index > lastIndex) {
      out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex, m.index)}</span>);
    }
    if (m[1] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__key">
          {m[1]}
        </span>,
        <span key={`t-${idx++}`}>:</span>
      );
    } else if (m[2] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__str">
          {m[2]}
        </span>
      );
    } else if (m[3] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__bool">
          {m[3]}
        </span>
      );
    } else if (m[4] !== undefined) {
      out.push(
        <span key={`t-${idx++}`} className="syslog-json__num">
          {m[4]}
        </span>
      );
    }
    lastIndex = tokenRe.lastIndex;
  }
  if (lastIndex < json.length) {
    out.push(<span key={`t-${idx++}`}>{json.slice(lastIndex)}</span>);
  }
  return out;
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
